import { useState, useEffect } from "@wordpress/element";
import { X, CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";

const Toast = ({ toast, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);

    // Animation on mount
    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleRemove = () => {
        setIsExiting(true);
        setTimeout(() => {
            onRemove(toast.id);
        }, 300); // Match animation duration
    };

    const getToastStyles = () => {
        const baseStyles =
            "relative flex items-start p-4 rounded-lg shadow-lg border max-w-sm w-full transform transition-all duration-300 ease-in-out";

        const typeStyles = {
            success: "bg-green-50 border-green-200 text-green-800",
            error: "bg-red-50 border-red-200 text-red-800",
            warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
            info: "bg-blue-50 border-blue-200 text-blue-800",
        };

        const animationStyles = isExiting
            ? "translate-x-full opacity-0"
            : isVisible
            ? "translate-x-0 opacity-100"
            : "translate-x-full opacity-0";

        return `${baseStyles} ${typeStyles[toast.type]} ${animationStyles}`;
    };

    const getIcon = () => {
        const iconProps = { className: "w-5 h-5 mr-3 mt-0.5 flex-shrink-0" };

        switch (toast.type) {
            case "success":
                return (
                    <CheckCircle
                        {...iconProps}
                        className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-green-500"
                    />
                );
            case "error":
                return (
                    <XCircle
                        {...iconProps}
                        className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-red-500"
                    />
                );
            case "warning":
                return (
                    <AlertTriangle
                        {...iconProps}
                        className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-yellow-500"
                    />
                );
            case "info":
                return (
                    <Info
                        {...iconProps}
                        className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-blue-500"
                    />
                );
            default:
                return (
                    <Info
                        {...iconProps}
                        className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-blue-500"
                    />
                );
        }
    };

    return (
        <div className={getToastStyles()}>
            {getIcon()}

            <div className="flex-1 min-w-0">
                {toast.title && (
                    <div className="font-semibold text-sm mb-1">
                        {toast.title}
                    </div>
                )}
                <div className="text-sm">{toast.message}</div>
                {toast.action && (
                    <div className="mt-2">
                        <button
                            onClick={toast.action.handler}
                            className="text-xs font-medium underline hover:no-underline"
                        >
                            {toast.action.label}
                        </button>
                    </div>
                )}
            </div>

            <button
                onClick={handleRemove}
                className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

export default Toast;
